import { el, clear } from '../../utils/dom.js';
import { Reports, Exports } from '../../api/resources.js';
import { fmtDate, fmtMoney, fmtNumber } from '../../utils/format.js';
import { dataTable } from '../../components/table.js';
import { statusBadge } from '../../components/badge.js';
import { ok, err } from '../../components/toast.js';
import { downloadFile } from '../../api/client.js';

const TABS = [
  { key: 'inventory',   label: 'Inventario' },
  { key: 'rentals',     label: 'Rentas' },
  { key: 'loans',       label: 'Prestamos' },
  { key: 'maintenance', label: 'Mantenimiento' },
  { key: 'financial',   label: 'Financiero' },
  { key: 'top-rented',  label: 'Top rentados' },
];

export default async function ReportsPage(outlet) {
  clear(outlet);
  let activeTab = 'inventory';

  outlet.appendChild(el('div', { class: 'page-head' }, el('h1', {}, 'Reportes')));

  const tabsBar = el('div', { class: 'flex gap-1 mb-3', style: 'border-bottom: 1px solid var(--border); padding-bottom: 0;' });
  for (const t of TABS) {
    const btn = el('button', {
      class: 'btn ghost',
      style: t.key === activeTab ? 'background: var(--surface-2)' : '',
      onclick: () => { activeTab = t.key; setActive(); load(); },
      dataset: { tab: t.key },
    }, t.label);
    tabsBar.appendChild(btn);
  }
  outlet.appendChild(tabsBar);

  function setActive() {
    [...tabsBar.children].forEach((b) => {
      b.style.background = b.dataset.tab === activeTab ? 'var(--surface-2)' : '';
    });
  }

  // Filtros simples (fechas)
  const fromInput = el('input', { class: 'input', type: 'date' });
  const toInput   = el('input', { class: 'input', type: 'date' });
  const refreshBtn = el('button', { class: 'btn', onclick: () => load() }, 'Actualizar');

  async function exportAs(format) {
    if (activeTab === 'top-rented') { err('Sin export disponible'); return; }
    const params = { format, from_date: fromInput.value || undefined, to_date: toInput.value || undefined };
    try {
      await downloadFile(Exports.path(activeTab, params));
      ok(`Exportado (${format.toUpperCase()})`);
    } catch (ex) { err(ex.message); }
  }
  const csvBtn  = el('button', { class: 'btn', onclick: () => exportAs('csv') },  '⬇ CSV');
  const xlsxBtn = el('button', { class: 'btn', onclick: () => exportAs('xlsx') }, '⬇ Excel');

  outlet.appendChild(el('div', { class: 'toolbar' },
    labelWrap('Desde', fromInput), labelWrap('Hasta', toInput), refreshBtn,
    el('div', { class: 'flex-1' }),
    csvBtn, xlsxBtn,
  ));

  const host = el('div');
  outlet.appendChild(host);

  function labelWrap(label, ctrl) {
    return el('label', { class: 'flex items-center gap-2 muted text-sm' }, label, ctrl);
  }

  async function load() {
    host.innerHTML = '<div class="empty">Cargando...</div>';
    const params = { from_date: fromInput.value || undefined, to_date: toInput.value || undefined };
    try {
      const data = await Reports[fnFor(activeTab)](params);
      host.replaceChildren(renderTab(activeTab, data));
    } catch (ex) {
      host.innerHTML = `<div class="empty">Error: ${ex.message}</div>`;
      err(ex.message);
    }
  }
  load();
}

function fnFor(tab) {
  if (tab === 'top-rented') return 'topRented';
  return tab.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

function renderTab(tab, data) {
  if (tab === 'inventory') return renderInventory(data);
  if (tab === 'rentals')   return renderRentals(data);
  if (tab === 'loans')     return renderLoans(data);
  if (tab === 'maintenance') return renderMaintenance(data);
  if (tab === 'financial') return renderFinancial(data);
  if (tab === 'top-rented') return renderTopRented(data);
  return el('pre', {}, JSON.stringify(data, null, 2));
}

function renderInventory(data) {
  const wrap = document.createDocumentFragment();
  const items = data.items || [];
  const units = data.units || [];

  const itemsCard = el('div', { class: 'card mb-4' },
    el('div', { class: 'card-head' }, el('h3', {}, `Items (${items.length})`)),
    dataTable({
      columns: [
        { key: 'name', label: 'Item' },
        { key: 'kind', label: 'Tipo' },
        { key: 'category', label: 'Categoria', render: (r) => r.category || '—' },
        { key: 'internal_code', label: 'Codigo' },
        { key: 'quantity_total', label: 'Total', className: 'right' },
        { key: 'min_stock', label: 'Min', className: 'right' },
        { key: 'is_active', label: 'Activo', render: (r) => r.is_active ? 'Si' : 'No' },
      ],
      rows: items, empty: 'Sin items.',
    }),
  );

  const unitsCard = el('div', { class: 'card' },
    el('div', { class: 'card-head' }, el('h3', {}, `Unidades (${units.length})`)),
    dataTable({
      columns: [
        { key: 'item_name', label: 'Item' },
        { key: 'serial_number', label: 'Serial', render: (r) => el('span', { class: 'text-mono' }, r.serial_number || '—') },
        { key: 'internal_code', label: 'Codigo' },
        { key: 'status', label: 'Estado', render: (r) => statusBadge(r.status) },
        { key: 'location_name', label: 'Ubicacion' },
      ],
      rows: units, empty: 'Sin unidades.',
    }),
  );

  const root = el('div');
  root.append(itemsCard, unitsCard);
  return root;
}

function renderRentals(data) {
  const root = el('div');
  if (data.summary) {
    root.appendChild(el('div', { class: 'stats' },
      stat('Rentas',        fmtNumber(data.summary.count)),
      stat('Facturado',     fmtMoney(data.summary.total_billed)),
      stat('Cobrado',       fmtMoney(data.summary.total_collected), 'success'),
      stat('Pendiente',     fmtMoney(data.summary.total_pending), 'danger'),
    ));
  }
  root.appendChild(el('div', { class: 'card' },
    dataTable({
      columns: [
        { key: 'code', label: 'Codigo', render: (r) => el('a', { href: `#/rentals/${r.id}`, class: 'text-mono' }, r.code) },
        { key: 'client_name', label: 'Cliente' },
        { key: 'status', label: 'Estado', render: (r) => statusBadge(r.status) },
        { key: 'start_date', label: 'Inicio', render: (r) => fmtDate(r.start_date) },
        { key: 'end_date',   label: 'Fin',    render: (r) => fmtDate(r.end_date) },
        { key: 'total',      label: 'Total',  className: 'right', render: (r) => fmtMoney(r.total) },
        { key: 'paid_amount',label: 'Pagado', className: 'right', render: (r) => fmtMoney(r.paid_amount) },
        { key: 'balance',    label: 'Saldo',  className: 'right', render: (r) => fmtMoney(r.balance) },
      ],
      rows: data.rentals || [],
    }),
  ));
  return root;
}

function renderLoans(data) {
  return el('div', { class: 'card' },
    dataTable({
      columns: [
        { key: 'code', label: 'Codigo', render: (r) => el('a', { href: `#/loans/${r.id}`, class: 'text-mono' }, r.code) },
        { key: 'responsible_name', label: 'Responsable' },
        { key: 'position', label: 'Cargo', render: (r) => r.position || '—' },
        { key: 'status', label: 'Estado', render: (r) => statusBadge(r.status) },
        { key: 'start_date', label: 'Inicio', render: (r) => fmtDate(r.start_date) },
        { key: 'expected_return_date', label: 'Esperado', render: (r) => fmtDate(r.expected_return_date) },
        { key: 'actual_return_date', label: 'Devuelto', render: (r) => r.actual_return_date ? fmtDate(r.actual_return_date) : '—' },
      ],
      rows: data.loans || [],
    }),
  );
}

function renderMaintenance(data) {
  const root = el('div');
  if (data.summary) {
    root.appendChild(el('div', { class: 'stats' },
      stat('Registros', fmtNumber(data.summary.count)),
      stat('Costo total', fmtMoney(data.summary.total_cost)),
    ));
  }
  root.appendChild(el('div', { class: 'card' },
    dataTable({
      columns: [
        { key: 'code', label: 'Codigo', render: (r) => el('a', { href: `#/maintenance/${r.id}`, class: 'text-mono' }, r.code) },
        { key: 'item_name', label: 'Equipo' },
        { key: 'type',   label: 'Tipo' },
        { key: 'status', label: 'Estado', render: (r) => statusBadge(r.status) },
        { key: 'cost',   label: 'Costo', className: 'right', render: (r) => fmtMoney(r.cost) },
        { key: 'technician', label: 'Tecnico', render: (r) => r.technician || '—' },
        { key: 'start_date', label: 'Inicio', render: (r) => r.start_date ? fmtDate(r.start_date) : '—' },
      ],
      rows: data.records || [],
    }),
  ));
  return root;
}

function renderFinancial(data) {
  const root = el('div');
  root.appendChild(el('div', { style: 'display:grid;grid-template-columns:repeat(2,1fr);gap:var(--sp-4);' },
    el('div', { class: 'card' },
      el('div', { class: 'card-head' }, el('h3', {}, 'Pagos por metodo')),
      dataTable({
        columns: [
          { key: 'method', label: 'Metodo' },
          { key: 'count',  label: 'Cantidad', className: 'right' },
          { key: 'total',  label: 'Total',    className: 'right', render: (r) => fmtMoney(r.total) },
        ],
        rows: data.payments_by_method || [],
      }),
    ),
    el('div', { class: 'card' },
      el('div', { class: 'card-head' }, el('h3', {}, 'Penalidades por tipo')),
      dataTable({
        columns: [
          { key: 'type',  label: 'Tipo' },
          { key: 'count', label: 'Cantidad', className: 'right' },
          { key: 'total', label: 'Total',    className: 'right', render: (r) => fmtMoney(r.total) },
        ],
        rows: data.penalties_by_type || [],
      }),
    ),
  ));
  root.appendChild(el('div', { class: 'card mt-4' },
    el('div', { class: 'card-head' }, el('h3', {}, 'Deudores')),
    dataTable({
      columns: [
        { key: 'client_name', label: 'Cliente' },
        { key: 'phone',  label: 'Telefono', render: (r) => r.phone || '—' },
        { key: 'email',  label: 'Email',    render: (r) => r.email || '—' },
        { key: 'pending_rentals', label: 'Rentas pendientes', className: 'right' },
        { key: 'total_debt',      label: 'Deuda total',       className: 'right', render: (r) => fmtMoney(r.total_debt) },
      ],
      rows: data.debtors || [],
    }),
  ));
  return root;
}

function renderTopRented(data) {
  return el('div', { class: 'card' },
    dataTable({
      columns: [
        { key: 'name',   label: 'Item' },
        { key: 'brand',  label: 'Marca', render: (r) => r.brand || '—' },
        { key: 'model',  label: 'Modelo', render: (r) => r.model || '—' },
        { key: 'times_rented', label: 'Veces rentado', className: 'right' },
        { key: 'total_revenue', label: 'Ingreso total', className: 'right', render: (r) => fmtMoney(r.total_revenue) },
      ],
      rows: data || [],
    }),
  );
}

function stat(label, value, kind) {
  const colors = { success: 'var(--success)', danger: 'var(--danger)', warn: 'var(--warning)', primary: 'var(--primary)' };
  return el('div', { class: 'stat' },
    el('div', { class: 'label' }, label),
    el('div', { class: 'value', style: kind ? `color: ${colors[kind] || ''}` : '' }, String(value)),
  );
}
